/**
 * Keyed server messages (R2 localisation, audit chunk B5). The backend keeps writing its deterministic English
 * and attaches `{key, params}` next to it (aicio/i18n_keys.py: `<field>_i18n`, or `detail_i18n` on an error
 * body). This renders that key from copy/serverMessages in the member's language, with every number, amount,
 * percentage and date in that language's notation.
 *
 * English, a missing message, an unknown key or a template that fails to render shows the backend's own English
 * text — so English stays byte-identical to what the producer wrote, and nothing ever renders blank.
 */
import { getMyCIOLanguage } from '@/lib/mycio/language'
import * as Sentry from '@sentry/nextjs'
import { bandLabel } from '@/lib/mycio/bandLabel'
import { getMyCIOServerMessages } from '@/lib/mycio/copy/serverMessages'
import { formatCurrency, formatDate, formatMoneyCompact, formatNumber, formatPercent, resolveNumberLocale } from '@/lib/mycio/numberFormat'

export type ServerParam =
  | string
  | number
  | {
      t: 'money' | 'pct' | 'pp' | 'num' | 'year' | 'band' | 'date' | 'msg'
      v?: unknown
      ccy?: string
      f?: 'full'
      d?: number
      en?: string
      key?: string
      params?: Record<string, ServerParam>
    }

export type KeyedMessage = { key: string; params?: Record<string, ServerParam> }

// A single broken producer/catalog pair can render many times in one page. Report it once per
// process/page and locale, without attaching the message text or interpolation data.
const reportedFallbacks = new Set<string>()
const MAX_REPORTED_FALLBACKS = 256

type FallbackFailure = 'missing_catalog_key' | 'template_render_failure'

function reportFallback(key: string, language: string, failure: FallbackFailure): void {
  const dedupeKey = JSON.stringify([key, language, failure])
  if (reportedFallbacks.has(dedupeKey)) return
  // Bound process memory if a malformed producer sends arbitrary keys. Once full, safe
  // fallback rendering continues and telemetry stops for this process.
  if (reportedFallbacks.size >= MAX_REPORTED_FALLBACKS) return
  reportedFallbacks.add(dedupeKey)
  try {
    Sentry.captureMessage('Keyed server message used its safe fallback', {
      level: 'warning',
      // The event contains only the catalog key, normalized language, and failure kind.
      tags: { message_key: key, language, failure_kind: failure },
      fingerprint: ['keyed-server-message-fallback', key, language, failure],
    })
  } catch {
    // Diagnostics must never interfere with the producer's safe English/localized fallback.
  }
}

const isKeyed = (m: unknown): m is KeyedMessage =>
  !!m && typeof m === 'object' && typeof (m as KeyedMessage).key === 'string' && (m as KeyedMessage).key.includes('.')

function template(key: string, language: string): string | undefined {
  const dot = key.indexOf('.')
  const group = getMyCIOServerMessages(language)[key.slice(0, dot)] as Record<string, string> | undefined
  const t = group?.[key.slice(dot + 1)]
  return typeof t === 'string' && t ? t : undefined
}

function numeric(p: ServerParam | undefined): number | undefined {
  const v = typeof p === 'object' && p ? p.v : p
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN
  return Number.isFinite(n) ? n : undefined
}

function formatParam(p: ServerParam | undefined, language: string, locale: string): string {
  if (p === undefined || p === null) return ''
  if (typeof p === 'string') return p
  if (typeof p === 'number') return formatNumber(p, { locale })
  const n = numeric(p)
  switch (p.t) {
    case 'money':
      if (n === undefined) return p.en ?? ''
      return p.f === 'full' ? formatCurrency(n, { currency: p.ccy, locale, ...(p.d !== undefined ? { maximumFractionDigits: p.d } : {}) }) : formatMoneyCompact(n, { currency: p.ccy, locale })
    case 'pct':
      return n === undefined ? p.en ?? '' : formatPercent(n, { locale, maximumFractionDigits: p.d ?? 1 })
    case 'pp':
      return n === undefined ? p.en ?? '' : formatNumber(n * 100, { locale, maximumFractionDigits: p.d ?? 1, minimumFractionDigits: p.d ?? 1, signDisplay: 'exceptZero' })
    case 'num':
      return n === undefined ? p.en ?? '' : formatNumber(n, { locale, maximumFractionDigits: p.d ?? 2 })
    case 'year':
      return String(p.v ?? p.en ?? '')
    case 'band':
      return bandLabel(String(p.v ?? ''), language)
    case 'date': {
      const d = typeof p.v === 'string' || typeof p.v === 'number' ? new Date(p.v) : null
      return d && !Number.isNaN(d.getTime()) ? formatDate(d, { locale, dateStyle: 'medium' }) : p.en ?? ''
    }
    case 'msg': {
      if (!p.key) return p.en ?? ''
      // Typed messages have no enclosing producer sentence to fall back to.
      // Render their catalog in English too; renderKeyed intentionally defers
      // English prose to its producer and therefore cannot serve this path.
      if (language === 'en') {
        const text = template(p.key, language)
        if (text) {
          try { return fill(text, p.params ?? {}, language, locale) }
          catch { reportFallback(p.key, language, 'template_render_failure') }
        } else reportFallback(p.key, language, 'missing_catalog_key')
        return p.en ?? ''
      }
      return renderKeyed({ key: p.key, params: p.params }, language) ?? p.en ?? ''
    }
    default:
      return p.en ?? ''
  }
}

function matchBrace(s: string, i: number): number {
  let depth = 0
  for (let j = i; j < s.length; j++) {
    if (s[j] === '{') depth++
    else if (s[j] === '}' && --depth === 0) return j
  }
  throw new Error(`unbalanced template: ${s}`)
}

function options(body: string): Record<string, string> {
  const out: Record<string, string> = {}
  let i = 0
  while (i < body.length) {
    const m = /^\s*(=?[\w-]+)\s*\{/.exec(body.slice(i))
    if (!m) break
    const start = i + m[0].length - 1
    const end = matchBrace(body, start)
    out[m[1]] = body.slice(start + 1, end)
    i = end + 1
  }
  return out
}

function fill(t: string, params: Record<string, ServerParam>, language: string, locale: string): string {
  let out = ''
  for (let i = 0; i < t.length; ) {
    if (t[i] !== '{') { out += t[i++]; continue }
    const end = matchBrace(t, i)
    const inner = t.slice(i + 1, end)
    const m = /^\s*(\w+)\s*,\s*(plural|select)\s*,([\s\S]*)$/.exec(inner)
    if (m) {
      const [, name, kind, body] = m
      const opts = options(body)
      const p = params[name]
      if (kind === 'plural') {
        const n = numeric(p)
        const cat = n === undefined ? 'other' : new Intl.PluralRules(locale).select(n)
        const exact = n !== undefined ? opts[`=${n}`] : undefined
        const pick = exact ?? opts[cat] ?? opts.other ?? ''
        out += fill(pick.replace(/#/g, formatParam(p, language, locale)), params, language, locale)
      } else {
        const key = typeof p === 'object' && p ? String(p.v ?? p.en ?? '') : String(p ?? '')
        out += fill(opts[key] ?? opts.other ?? '', params, language, locale)
      }
    } else {
      const name = inner.trim()
      if (!(name in params)) throw new Error(`missing param ${name}`)
      out += formatParam(params[name], language, locale)
    }
    i = end + 1
  }
  return out
}

/**
 * One typed server param (`ServerParam`) written in the member's language and notation. English too: a reply's figure
 * segments carry no English sentence around them, so the page formats the number itself (money in whole units
 * with its currency, a percentage, a year) exactly as it does inside a keyed template.
 */
export function renderServerParam(p: ServerParam | undefined, language: string | null | undefined): string {
  const code = getMyCIOLanguage(language).code
  return formatParam(p, code, resolveNumberLocale(code))
}

/** The message in `language`, or null when that language has no template for it (the caller shows English). */
export function renderKeyed(m: KeyedMessage | null | undefined, language: string | null | undefined): string | null {
  if (!isKeyed(m)) return null
  const code = getMyCIOLanguage(language).code
  if (code === 'en') return null
  const t = template(m.key, code)
  if (!t) {
    reportFallback(m.key, code, 'missing_catalog_key')
    return null
  }
  try {
    return fill(t, m.params ?? {}, code, resolveNumberLocale(code))
  } catch {
    reportFallback(m.key, code, 'template_render_failure')
    return null
  }
}

const isTypedParam = (m: unknown): m is Exclude<ServerParam, string | number> =>
  !!m && typeof m === 'object' && typeof (m as { t?: unknown }).t === 'string' && typeof (m as { key?: unknown }).key !== 'string'

/**
 * One table cell. A typed param (money, percentage ...) is written by the page in the member's language and
 * notation, English included. A keyed message that carries a typed param is filled the same way in English, so a
 * column never mixes the backend's "USD 42,000" with the page's "$42,000"; any other keyed cell is unchanged.
 */
export function serverCellText(text: string | null | undefined, m: unknown, language: string | null | undefined): string {
  const code = getMyCIOLanguage(language).code
  if (isTypedParam(m)) {
    const out = formatParam(m, code, resolveNumberLocale(code))
    return out || text || ''
  }
  if (code === 'en' && isKeyed(m) && Object.values(m.params ?? {}).some((v) => typeof v === 'object' && v !== null)) {
    const t = template(m.key, code)
    if (t) {
      try { return fill(t, m.params ?? {}, code, resolveNumberLocale(code)) } catch { reportFallback(m.key, code, 'template_render_failure') }
    }
  }
  return serverText(text, m, language)
}

/** Backend English `text`, or its keyed message rendered in the member's language when there is one. */
export function serverText(text: string | null | undefined, m: unknown, language: string | null | undefined): string {
  const localized = isKeyed(m) ? renderKeyed(m, language) : null
  if (localized !== null) return localized
  if (text) return text
  // Key-only blocks (notices and links) have no producer sentence to fall back to in English.
  if (isKeyed(m)) {
    const english = template(m.key, 'en')
    if (english) {
      try { return fill(english, m.params ?? {}, 'en', resolveNumberLocale('en')) }
      catch { reportFallback(m.key, 'en', 'template_render_failure') }
    } else reportFallback(m.key, 'en', 'missing_catalog_key')
  }
  return text ?? ''
}

/** A list of backend English strings with a parallel list of keyed messages (`<field>_i18n`). */
export function serverTextList(texts: readonly string[] | null | undefined, ms: unknown, language: string | null | undefined): string[] {
  const keyed = Array.isArray(ms) ? ms : []
  return (texts ?? []).map((t, i) => serverText(t, keyed[i], language))
}

/** An object field with its `<field>_i18n` sibling: serverField(row, 'summary', language). */
export function serverField(obj: unknown, field: string, language: string | null | undefined): string {
  if (!obj || typeof obj !== 'object') return ''
  const o = obj as Record<string, unknown>
  const v = o[field]
  return serverText(typeof v === 'string' ? v : v == null ? '' : String(v), o[`${field}_i18n`], language)
}

/**
 * An error body's sentence: `detail_i18n` in the member's language when the backend keyed it, else the English
 * `detail` (a string, or a dict's `message`), `error` or `message`, else `fallback`. In another language an
 * unkeyed body yields `fallback` (the caller's translated line) rather than English. Raw snake_case codes are
 * never returned.
 */
export function serverErrorText(body: unknown, language: string | null | undefined, fallback?: string): string | undefined {
  if (!body || typeof body !== 'object') return fallback
  const b = body as Record<string, unknown>
  const keyed = renderKeyed(isKeyed(b.detail_i18n) ? b.detail_i18n : undefined, language)
  if (keyed) return keyed
  // Another language with no keyed sentence: the caller's own (translated) fallback beats backend English.
  if (fallback !== undefined && getMyCIOLanguage(language).code !== 'en') return fallback
  // `detail` may be the English sentence, or a dict carrying a machine `reason` and the English `message`.
  const nested = b.detail && typeof b.detail === 'object' ? (b.detail as Record<string, unknown>).message : undefined
  for (const v of [b.detail, nested, b.error, b.message]) {
    if (typeof v === 'string' && v.trim() && !/^[a-z0-9_]+$/.test(v.trim())) return v
  }
  return fallback
}
